import axios from "axios";
import { ApiReqBaseUrl } from "../../../assets/GlobalVariables";

/**
 * LDS document routes live on the API host root (same as Orders PDF download),
 * NOT under `/api/...`. `resolveApiUrl('/lds_orders/...')` wrongly injects `/api`
 * and produces Flask "Not Found" in CRM document preview.
 */
function ldsOrderApiUrl(orderId, suffix) {
  const base = String(ApiReqBaseUrl || "").replace(/\/$/, "");
  const id = encodeURIComponent(String(orderId || "").trim());
  const path = String(suffix || "").replace(/^\//, "");
  return `${base}/lds_orders/${id}/${path}`;
}

/**
 * Yad Sarig (Hyp) fiscal receipt — NEVER HotelsRiver booking confirmation.
 * @returns {Promise<{blob?: Blob, redirectUrl?: string, contentType?: string, hypStatus?: string, transId?: string}>}
 */
export async function fetchYaadSarigReceipt(orderId) {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("order_id_required");

  const url = ldsOrderApiUrl(id, "yaad-hesh?format=json");

  // First ask JSON meta so we never mistake HotelsRiver PDF for Yaad receipt
  try {
    const metaRes = await axios.get(url, {
      withCredentials: true,
      timeout: 60000,
      headers: { Accept: "application/json" },
      validateStatus: (s) => s === 200 || s === 404,
    });
    const meta = metaRes.data || {};
    if (metaRes.status === 404 || meta.status === "error") {
      const raw =
        meta.hyp_status || meta.error || meta.message || "yaad_hesh_not_available";
      const msg =
        String(raw).trim().toLowerCase() === "not found"
          ? "לא נמצאה קבלה מיעד שריג להזמנה זו"
          : raw;
      const err = new Error(msg);
      err.hypStatus = meta.hyp_status || meta.error || msg;
      err.transId = meta.trans_id;
      throw err;
    }
    if (meta.status === "ok" && meta.kind === "url" && meta.url) {
      return {
        redirectUrl: String(meta.url),
        contentType: "text/url",
        transId: meta.trans_id,
      };
    }
  } catch (err) {
    if (err?.hypStatus || err?.message?.includes("יעד שריג") || err?.message?.includes("PDF")) {
      throw err;
    }
    // fall through to binary fetch if meta endpoint behaved oddly
    if (err?.response?.data?.hyp_status || err?.response?.data?.error) {
      const data = err.response.data;
      const e2 = new Error(data.hyp_status || data.error || "yaad_hesh_not_available");
      e2.hypStatus = data.hyp_status || data.error;
      e2.transId = data.trans_id;
      throw e2;
    }
  }

  // Binary document (PDF/HTML)
  const binUrl = ldsOrderApiUrl(id, "yaad-hesh");
  let res;
  try {
    res = await axios.get(binUrl, {
      responseType: "blob",
      withCredentials: true,
      timeout: 60000,
      validateStatus: (s) => s === 200,
    });
  } catch (err) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        const raw =
          parsed?.hyp_status || parsed?.error || parsed?.message || "yaad_hesh_not_available";
        const msg =
          String(raw).trim().toLowerCase() === "not found"
            ? "לא נמצאה קבלה מיעד שריג להזמנה זו"
            : raw;
        const e2 = new Error(msg);
        e2.hypStatus = parsed?.hyp_status || parsed?.error || msg;
        e2.transId = parsed?.trans_id;
        throw e2;
      } catch (inner) {
        if (inner?.hypStatus) throw inner;
        throw new Error("yaad_hesh_not_available");
      }
    }
    const statusText = String(err?.response?.statusText || err?.message || "").trim();
    if (statusText.toLowerCase() === "not found") {
      throw new Error("לא נמצאה קבלה מיעד שריג להזמנה זו");
    }
    throw err;
  }

  const contentType = String(res.headers?.["content-type"] || "");
  const docKind = String(res.headers?.["x-document-kind"] || "");
  if (contentType.includes("application/json")) {
    const text = await res.data.text();
    const parsed = JSON.parse(text);
    if (parsed?.status === "ok" && parsed?.url) {
      return { redirectUrl: String(parsed.url), contentType: "text/url" };
    }
    throw new Error(parsed?.hyp_status || parsed?.error || "yaad_hesh_not_available");
  }

  // Guard: never treat HotelsRiver confirmation PDF as Yaad receipt
  if (docKind && docKind !== "yaad-sarig-hesh") {
    throw new Error("unexpected_document_kind");
  }

  return {
    blob: new Blob([res.data], {
      type: contentType || "application/pdf",
    }),
    contentType,
  };
}

/** HotelsRiver booking confirmation PDF (אישור הזמנה) — separate from Yad Sarig. */
export async function fetchLdsOrderConfirmationPdf(orderId, totalPrice) {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("order_id_required");
  const q =
    totalPrice != null && totalPrice !== ""
      ? `?totalPrice=${encodeURIComponent(String(totalPrice))}`
      : "";
  const url = `${ldsOrderApiUrl(id, "download-pdf")}${q}`;
  const res = await axios.get(url, {
    responseType: "blob",
    withCredentials: true,
    timeout: 60000,
    validateStatus: (s) => s === 200,
  });
  if (res.status !== 200) {
    throw new Error("pdf_download_failed");
  }
  const contentType = String(res.headers?.["content-type"] || "");
  if (contentType.includes("application/json")) {
    throw new Error("pdf_download_failed");
  }
  return new Blob([res.data], { type: "application/pdf" });
}

/**
 * Agent hotel voucher PDF (שובר מלון) — hotel reservation layout without net/price.
 * Campaign hotel packages only.
 */
export async function fetchLdsOrderHotelVoucherPdf(orderId) {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("order_id_required");
  const url = ldsOrderApiUrl(id, "hotel-voucher");
  let res;
  try {
    res = await axios.get(url, {
      responseType: "blob",
      withCredentials: true,
      timeout: 60000,
      validateStatus: (s) => s === 200 || s === 404,
    });
  } catch (err) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        throw new Error(
          parsed?.message || parsed?.error || "hotel_voucher_unavailable"
        );
      } catch (inner) {
        if (inner?.message && inner.message !== "Unexpected end of JSON input") {
          throw inner;
        }
      }
    }
    throw err;
  }
  if (res.status === 404) {
    let msg = "שובר מלון לא זמין להזמנה זו";
    try {
      const text = await res.data.text();
      const parsed = JSON.parse(text);
      msg = parsed?.message || parsed?.error || msg;
    } catch {
      /* keep default */
    }
    throw new Error(msg);
  }
  const contentType = String(res.headers?.["content-type"] || "");
  if (contentType.includes("application/json")) {
    const text = await res.data.text();
    const parsed = JSON.parse(text);
    throw new Error(parsed?.message || parsed?.error || "hotel_voucher_unavailable");
  }
  return new Blob([res.data], { type: "application/pdf" });
}

/**
 * Agent-branded order summary PDF (סיכום הזמנה) — no HotelsRiver branding.
 */
export async function fetchLdsOrderSummaryPdf(orderId) {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("order_id_required");
  const url = ldsOrderApiUrl(id, "order-summary");
  const res = await axios.get(url, {
    responseType: "blob",
    withCredentials: true,
    timeout: 60000,
    validateStatus: (s) => s === 200,
  });
  if (res.status !== 200) {
    throw new Error("order_summary_download_failed");
  }
  const contentType = String(res.headers?.["content-type"] || "");
  if (contentType.includes("application/json")) {
    throw new Error("order_summary_download_failed");
  }
  return new Blob([res.data], { type: "application/pdf" });
}

/**
 * CRM: email confirmation and/or Yad receipt and/or hotel voucher / order summary to an explicit address.
 * @param {string} orderId
 * @param {{ toEmail: string, documents?: Array<'confirmation'|'receipt'|'hotel_voucher'|'order_summary'>, sendConfirmation?: boolean, sendReceipt?: boolean, sendHotelVoucher?: boolean, sendOrderSummary?: boolean }} opts
 */
export async function sendCrmOrderDocuments(orderId, opts = {}) {
  const id = String(orderId || "").trim();
  if (!id) throw new Error("order_id_required");
  const toEmail = String(opts.toEmail || opts.email || "").trim();
  if (!toEmail || !toEmail.includes("@")) throw new Error("invalid_email");

  const payload = {
    to_email: toEmail,
  };
  if (Array.isArray(opts.documents) && opts.documents.length) {
    payload.documents = opts.documents;
  } else {
    payload.send_confirmation = opts.sendConfirmation !== false;
    payload.send_receipt = Boolean(opts.sendReceipt);
    payload.send_hotel_voucher = Boolean(opts.sendHotelVoucher);
    payload.send_order_summary = Boolean(opts.sendOrderSummary);
  }

  const url = ldsOrderApiUrl(id, "crm-send-documents");
  const res = await axios.post(url, payload, {
    withCredentials: true,
    timeout: 90000,
    headers: { "Content-Type": "application/json" },
    validateStatus: (s) => s < 500,
  });
  const data = res.data || {};
  if (res.status >= 400 && data.status !== "partial") {
    const err = new Error(
      data.error ||
        data.receipt?.error ||
        data.confirmation?.error ||
        data.hotel_voucher?.error ||
        data.order_summary?.error ||
        "send_failed"
    );
    err.payload = data;
    throw err;
  }
  return data;
}
